/**
 * Photos, click-to-count and the event system (docs/automatedanalysis.md, Chosen design):
 * calibrations and capture sessions, photos in R2, objects on photos (analyst clicks and
 * runner suggestions), analysis jobs that runners claim with a runner key, the pipeline per
 * organism group with its autonomy level, and the reference-set reports. DOM-free: shared by
 * the Worker, the SPA and the runners.
 *
 * Pixel coordinates are in the photo's view image, which has the same pixel grid as the
 * original still: x to the right, y down, origin at the top-left corner.
 */
import { z } from "zod";
import {
  ANALYSIS_GROUPS,
  BACTERIA_CLASSES,
  dilutionSchema,
  HYPHA_COLORS,
  HYPHA_KINDS,
  ORGANISM_STATES,
  type AnalysisGroup,
  type HyphaColor,
  type HyphaKind,
  type OrganismState,
} from "./schema.ts";

// ---------------------------------------------------------------------------
// Capture: calibrations, sessions and photos
// ---------------------------------------------------------------------------

/** Objective magnifications photos are taken at; 1000× (oil) only for cell sizes and close-ups. */
export const MAGNIFICATIONS = [100, 400, 1000] as const;
export type Magnification = (typeof MAGNIFICATIONS)[number];
const magnificationSchema = z.union([z.literal(100), z.literal(400), z.literal(1000)]);

/**
 * still: one field of view; clip: a short video of one field (its view is the reference frame
 * objects are counted on); scan_tile: one tile of a motorised-stage scan of a whole coverslip.
 */
export const PHOTO_KINDS = ["still", "clip", "scan_tile"] as const;
export type PhotoKind = (typeof PHOTO_KINDS)[number];

/** What browsers show and runners annotate. */
export const VIEW_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type ViewType = (typeof VIEW_TYPES)[number];

/** Every original is kept; TIFF stills and clips need a separate view rendition. */
export const ORIGINAL_TYPES = [
  ...VIEW_TYPES,
  "image/tiff",
  "video/mp4",
  "video/quicktime",
  "video/webm",
] as const;
export type OriginalType = (typeof ORIGINAL_TYPES)[number];

/** Largest upload request body: the manifest and every file part together. */
export const MAX_UPLOAD_BYTES = 48 * 1024 * 1024;
export const MAX_PHOTOS_PER_UPLOAD = 50;
/** Largest side (px) of a view image. */
export const MAX_PHOTO_SIDE_PX = 20_000;

/** Area (µm²) one frame of `widthPx` × `heightPx` covers. */
export const imagedAreaUm2 = (widthPx: number, heightPx: number, umPerPixel: number): number =>
  widthPx * heightPx * umPerPixel * umPerPixel;

export const calibrationInputSchema = z.object({
  camera: z.string().trim().min(1).max(120),
  magnification: magnificationSchema,
  /** Measured with a stage micrometer for this objective and camera. */
  umPerPixel: z.number().positive().max(50),
  frameWidthPx: z.number().int().min(16).max(MAX_PHOTO_SIDE_PX),
  frameHeightPx: z.number().int().min(16).max(MAX_PHOTO_SIDE_PX),
  measuredAt: z.number().int().positive(),
  notes: z.string().trim().max(2000),
});
export type CalibrationInput = z.infer<typeof calibrationInputSchema>;

/** Calibrations are never edited, only retired, so every photo keeps the one it was taken with. */
export interface Calibration extends CalibrationInput {
  id: string;
  /** imagedAreaUm2(frameWidthPx, frameHeightPx, umPerPixel). */
  imagedAreaUm2: number;
  retiredAt: number | null;
  createdAt: number;
}

/** Scale a calibration to a resized or rotated photo of the same frame (1% aspect tolerance). */
export const photoUmPerPixel = (
  calibration: Pick<Calibration, "umPerPixel" | "frameWidthPx" | "frameHeightPx">,
  size: { widthPx: number; heightPx: number },
): number | null => {
  const frameLong = Math.max(calibration.frameWidthPx, calibration.frameHeightPx);
  const frameShort = Math.min(calibration.frameWidthPx, calibration.frameHeightPx);
  const photoLong = Math.max(size.widthPx, size.heightPx);
  const photoShort = Math.min(size.widthPx, size.heightPx);
  const frameAspect = frameLong / frameShort;
  const photoAspect = photoLong / photoShort;
  if (Math.abs(photoAspect / frameAspect - 1) > 0.01) return null;
  return calibration.umPerPixel * (frameLong / photoLong);
};

export const SESSION_STATUSES = ["open", "closed"] as const;
type SessionStatus = (typeof SESSION_STATUSES)[number];

export const startSessionInputSchema = z
  .object({
    group: z.enum(ANALYSIS_GROUPS),
    magnification: magnificationSchema,
    calibrationId: z.string().min(1),
    /** Drop (coverslip) the photos come from; required for nematodes, whose unit is the drop. */
    dropNumber: z.number().int().min(1).max(50).nullable(),
  })
  .refine((session) => session.group !== "nematodes" || session.dropNumber !== null, {
    path: ["dropNumber"],
    message: "A nematode session needs the drop number",
  });
export type StartSessionInput = z.infer<typeof startSessionInputSchema>;

/**
 * Multipart reference uploads add these fields to the normal photo manifest and file parts. The
 * dilution (mL per g) applies to every photo of the upload; browser uploads require it, runner
 * imports may omit it (empty or missing → null).
 */
export const referencePhotoUploadInputSchema = z.object({
  group: z.enum(ANALYSIS_GROUPS),
  magnification: z.coerce.number().pipe(magnificationSchema),
  dilution: z.coerce.number().pipe(dilutionSchema).nullable().default(null),
});
export type ReferencePhotoUploadInput = z.infer<typeof referencePhotoUploadInputSchema>;

/** At most one session is open at a time: the lab has one camera, so it is where new files go. */
export interface CaptureSession {
  id: string;
  researchId: string;
  group: AnalysisGroup;
  magnification: Magnification;
  calibrationId: string;
  camera: string;
  umPerPixel: number;
  dropNumber: number | null;
  status: SessionStatus;
  openedAt: number;
  closedAt: number | null;
  openedByName: string | null;
  photoCount: number;
}

/** The open session as the folder uploader sees it (no owner or land data). */
export interface OpenSession {
  id: string;
  group: AnalysisGroup;
  magnification: Magnification;
  dropNumber: number | null;
  camera: string;
  umPerPixel: number;
  /** Lab code of the sample, for the uploader's log. */
  sampleCode: string;
  /** Field number the next photo without one gets. */
  nextFieldNumber: number;
  openedAt: number;
}

const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/, "sha256 must be 64 lowercase hex digits");
const stageUmSchema = z.number().min(-1_000_000).max(1_000_000).nullable();

/** One file of an upload; entry i describes multipart parts `file:i` and `view:i`. */
const photoUploadItemSchema = z.object({
  /** File name as the camera or stage software saved it, without folders. */
  name: z.string().trim().min(1).max(255),
  /** Of the original bytes; the Worker checks it. */
  sha256: sha256Schema,
  kind: z.enum(PHOTO_KINDS),
  /** Null: the next field number of the session, in upload order. */
  fieldNumber: z.number().int().min(1).max(100_000).nullable(),
  /** Null: the session's drop number. */
  dropNumber: z.number().int().min(1).max(50).nullable(),
  /** Stage position of the image centre (µm), from the stage software. */
  stageXUm: stageUmSchema,
  stageYUm: stageUmSchema,
  capturedAt: z.number().int().positive().nullable(),
});
export type PhotoUploadItem = z.infer<typeof photoUploadItemSchema>;

export const photoUploadManifestSchema = z
  .array(photoUploadItemSchema)
  .min(1)
  .max(MAX_PHOTOS_PER_UPLOAD);

/**
 * Multipart parts of an upload: `manifest` (JSON PhotoUploadItem[]), `file:<i>` (the original)
 * and, when the original is not a view type (TIFF, clips), `view:<i>` (JPEG/PNG/WebP rendition
 * with the same pixel grid; for a clip its reference frame).
 */
export const UPLOAD_PARTS = {
  manifest: "manifest",
  file: (index: number): string => `file:${index}`,
  view: (index: number): string => `view:${index}`,
} as const;

export interface UploadOutcome {
  /** Index in the manifest. */
  index: number;
  name: string;
  /** existing: the research already has a photo with these bytes (retries are safe). */
  status: "created" | "existing" | "rejected";
  photoId: string | null;
  error: string | null;
}

/**
 * unchecked → ok | flagged (quick check) → ok | rejected (analyst). A rejected frame counts as
 * not examined.
 */
export const PHOTO_QUALITIES = ["unchecked", "ok", "flagged", "rejected"] as const;
export type PhotoQuality = (typeof PHOTO_QUALITIES)[number];

const QUALITY_FLAGS = [
  "blurry",
  "overexposed",
  "underexposed",
  "too_few_cells",
  "too_many_cells",
] as const;
export type QualityFlag = (typeof QUALITY_FLAGS)[number];

const share = z.number().min(0).max(1);

export const qualityReportSchema = z.object({
  metrics: z.object({
    /** Variance of the Laplacian of the downscaled luminance (domain/quality.ts). */
    focus: z.number().min(0),
    meanLuminance: z.number().min(0).max(255),
    overexposedShare: share,
    underexposedShare: share,
    /** Bacteria photos only: estimated cells in the frame. */
    densityEstimate: z.number().int().min(0).nullable(),
  }),
  flags: z.array(z.enum(QUALITY_FLAGS)).max(QUALITY_FLAGS.length),
});
export type QualityReport = z.infer<typeof qualityReportSchema>;

export const photoQualityInputSchema = z.object({ quality: z.enum(["ok", "rejected"]) });
export type PhotoQualityInput = z.infer<typeof photoQualityInputSchema>;

/** A reference photo's dilution; null clears it. */
export const photoDilutionInputSchema = z.object({ dilution: dilutionSchema.nullable() });
export type PhotoDilutionInput = z.infer<typeof photoDilutionInputSchema>;

// ---------------------------------------------------------------------------
// Objects on photos
// ---------------------------------------------------------------------------

/** point: one click (counts); polyline: a traced centre line (hyphae, nematodes); box: 2 corners. */
export const OBJECT_SHAPES = ["point", "polyline", "polygon", "box"] as const;
export type ObjectShape = (typeof OBJECT_SHAPES)[number];

/** Labelled so models learn what not to count; never counted. */
export const LOOK_ALIKES = [
  "debris",
  "mineral",
  "bubble",
  "root_hair",
  "plant_fibre",
  "spore",
] as const;
const PROTOZOA_CLASSES = ["flagellate", "naked_amoeba", "testate_amoeba", "ciliate"] as const;
const NEMATODE_CLASSES = [
  "bacterial_feeder",
  "fungal_feeder",
  "predatory",
  "root_feeder",
  "switcher",
  "bacterial_omnivore",
  "fungal_omnivore",
] as const;
/** A nematode whose feeding group is not known yet (e.g. stylet not visible at 100×). */
export const UNRESOLVED_NEMATODE = "nematode";

export const OBJECT_CLASSES = [
  ...BACTERIA_CLASSES,
  ...HYPHA_KINDS,
  ...PROTOZOA_CLASSES,
  ...NEMATODE_CLASSES,
  UNRESOLVED_NEMATODE,
  ...LOOK_ALIKES,
] as const;
export type ObjectClass = (typeof OBJECT_CLASSES)[number];

/** Classes that count toward AnalysisInput, per group. */
export const COUNTED_CLASSES: Record<AnalysisGroup, readonly ObjectClass[]> = {
  bacteria: BACTERIA_CLASSES,
  fungi: HYPHA_KINDS,
  protozoa: PROTOZOA_CLASSES,
  nematodes: NEMATODE_CLASSES,
};

const UNRESOLVED_CLASSES: readonly ObjectClass[] = [UNRESOLVED_NEMATODE];

/** Every class an object of the group may carry. */
export const classesFor = (group: AnalysisGroup): readonly ObjectClass[] => [
  ...COUNTED_CLASSES[group],
  ...(group === "nematodes" ? UNRESOLVED_CLASSES : []),
  ...LOOK_ALIKES,
];

export interface ObjectMark {
  class: ObjectClass;
  state: OrganismState | null;
  color: HyphaColor | null;
}

export const statesFor = (cls: ObjectClass): readonly OrganismState[] => {
  if (cls === "flagellate" || cls === "naked_amoeba" || cls === "ciliate") return ["cyst"];
  if (
    cls === "testate_amoeba" ||
    cls === UNRESOLVED_NEMATODE ||
    (NEMATODE_CLASSES as readonly ObjectClass[]).includes(cls)
  ) {
    return ["inactive"];
  }
  return [];
};

export const isHyphaKind = (cls: ObjectClass): cls is HyphaKind =>
  (HYPHA_KINDS as readonly ObjectClass[]).includes(cls);

/** Why explicit input is impossible; missing optional marks are valid. */
export const markIssue = (mark: ObjectMark): string | null => {
  if (mark.state !== null && !statesFor(mark.class).includes(mark.state)) {
    return "The state is not allowed for this class";
  }
  if (mark.color !== null && !isHyphaKind(mark.class)) {
    return "Only hyphae may have a colour";
  }
  return null;
};

/** Drop attributes a changed class cannot carry, preserving the rest of the object. */
export const fitMark = <T extends ObjectMark>(mark: T): T => ({
  ...mark,
  state: mark.state !== null && statesFor(mark.class).includes(mark.state) ? mark.state : null,
  color: isHyphaKind(mark.class) ? mark.color : null,
});

export const OBJECT_STATUSES = ["suggested", "accepted", "rejected"] as const;
export type ObjectStatus = (typeof OBJECT_STATUSES)[number];

export const OBJECT_SOURCES = ["runner", "analyst"] as const;
type ObjectSource = (typeof OBJECT_SOURCES)[number];

/**
 * Why a runner object waits for an analyst: low_confidence (below the step's threshold for its
 * class), needs_class (no counted class yet), audit (on a photo of the random audit sample),
 * disagreement (the second method saw it differently).
 */
export const REVIEW_FLAGS = ["low_confidence", "needs_class", "audit", "disagreement"] as const;
export type ReviewFlag = (typeof REVIEW_FLAGS)[number];

export type Point = [number, number];
const MAX_POINTS = 2000;
const pointSchema = z.tuple([
  z.number().min(0).max(MAX_PHOTO_SIDE_PX),
  z.number().min(0).max(MAX_PHOTO_SIDE_PX),
]);
const pointsSchema = z.array(pointSchema).min(1).max(MAX_POINTS);

/** Whether `count` points make the shape. */
const shapeFits = (shape: ObjectShape, count: number): boolean => {
  switch (shape) {
    case "point":
      return count === 1;
    case "box":
      return count === 2;
    case "polyline":
      return count >= 2;
    case "polygon":
      return count >= 3;
  }
};
const geometryIssue = { path: ["points"], message: "The points do not make the shape" };
const fitsShape = ({ shape, points }: { shape: ObjectShape; points: readonly Point[] }): boolean =>
  shapeFits(shape, points.length);

const probabilitiesSchema = z.partialRecord(z.enum(OBJECT_CLASSES), share);
const widthUmSchema = z.number().positive().max(100);
const lengthUmSchema = z.number().positive().max(100_000);

/** An analyst's click or traced line; the Worker computes lengthUm from the points. */
const newObjectSchema = z
  .object({
    /** Chosen by the client and echoed in ObjectBatchResult.created. */
    clientId: z.string().min(1).max(64),
    photoId: z.string().min(1),
    shape: z.enum(OBJECT_SHAPES),
    points: pointsSchema,
    class: z.enum(OBJECT_CLASSES),
    state: z.enum(ORGANISM_STATES).nullable().default(null),
    /** Hyphae: measured diameter (µm). */
    widthUm: widthUmSchema.nullable(),
    color: z.enum(HYPHA_COLORS).nullable(),
  })
  .refine(fitsShape, geometryIssue);
export type NewObject = z.infer<typeof newObjectSchema>;

/**
 * Own analyst objects change in place. A class, points, width, colour or state change
 * on a runner object is a correction: the Worker rejects the suggestion and adds an accepted
 * analyst object with `correctsId` pointing at it.
 */
const objectUpdateSchema = z.object({
  id: z.string().min(1),
  status: z.enum(["accepted", "rejected"]).optional(),
  class: z.enum(OBJECT_CLASSES).optional(),
  state: z.enum(ORGANISM_STATES).nullable().optional(),
  points: pointsSchema.optional(),
  widthUm: widthUmSchema.nullable().optional(),
  color: z.enum(HYPHA_COLORS).nullable().optional(),
});
export type ObjectUpdate = z.infer<typeof objectUpdateSchema>;

export const objectBatchInputSchema = z.object({
  create: z.array(newObjectSchema).max(500),
  update: z.array(objectUpdateSchema).max(500),
  /** Own analyst objects only; runner objects are rejected instead. */
  delete: z.array(z.string().min(1)).max(500),
  /** A field the analyst finished counting (or reopened). */
  counted: z.array(z.object({ photoId: z.string().min(1), counted: z.boolean() })).max(500),
});
export type ObjectBatchInput = z.infer<typeof objectBatchInputSchema>;

export interface PhotoObject {
  id: string;
  photoId: string;
  group: AnalysisGroup;
  shape: ObjectShape;
  points: Point[];
  class: ObjectClass;
  state: OrganismState | null;
  lengthUm: number | null;
  widthUm: number | null;
  color: HyphaColor | null;
  /** Runner objects: the model's confidence in `class` (0–1). */
  confidence: number | null;
  classProbabilities: Partial<Record<ObjectClass, number>> | null;
  /** The step's threshold for `class` when the object was suggested. */
  threshold: number | null;
  source: ObjectSource;
  /** The job purpose that made a runner object. */
  purpose: JobPurpose | null;
  /** Runner objects: the detector's model and version. */
  model: string | null;
  version: string | null;
  /** model@version that set `class` last, when a classifier did. */
  classifier: string | null;
  /** Analyst objects. */
  authorId: string | null;
  authorName: string | null;
  status: ObjectStatus;
  flags: ReviewFlag[];
  /** The runner suggestion this analyst object corrects. */
  correctsId: string | null;
  /** Null on an accepted runner object: accepted automatically by the autonomy level. */
  reviewedByName: string | null;
  reviewedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface ObjectBatchResult {
  /** Every object created or changed, corrections and corrected suggestions included. */
  objects: PhotoObject[];
  removed: string[];
  /** Photos whose counted state changed. */
  photos: PhotoSummary[];
  created: { clientId: string; id: string }[];
}

export const referenceSetInputSchema = z.object({ referenceSet: z.boolean() });
export type ReferenceSetInput = z.infer<typeof referenceSetInputSchema>;

// ---------------------------------------------------------------------------
// Jobs and runners
// ---------------------------------------------------------------------------

/**
 * quality: quick check of every photo; detect: the group's detector or outliner; classify: a
 * classifier for uncertain objects; audit_detect / audit_classify: the second, different method
 * on photos of the audit sample.
 */
export const STEP_ROLES = [
  "quality",
  "detect",
  "classify",
  "audit_detect",
  "audit_classify",
] as const;
export type StepRole = (typeof STEP_ROLES)[number];

/** evaluation: a candidate step run on reference-set photos; never reviewed or applied. */
export const JOB_PURPOSES = [...STEP_ROLES, "evaluation"] as const;
export type JobPurpose = (typeof JOB_PURPOSES)[number];

/** queued → leased → done; a lease that ends without an answer returns the job to the queue. */
export const JOB_STATUSES = ["queued", "leased", "done", "failed"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const MAX_JOB_ATTEMPTS = 3;
const DEFAULT_LEASE_SECONDS = 300;
const leaseSecondsSchema = z.number().int().min(30).max(3600);

export const MODEL_NAME = /^[a-z0-9][a-z0-9-]{0,47}$/;
const MODEL_VERSION = /^[0-9A-Za-z][0-9A-Za-z._-]{0,31}$/;
/** "name@version", e.g. "hyphae-trace@2"; runners only take jobs of exactly that version. */
export const MODEL_REF = /^[a-z0-9][a-z0-9-]{0,47}@[0-9A-Za-z][0-9A-Za-z._-]{0,31}$/;

export const modelRef = (model: string, version: string): string => `${model}@${version}`;

/** The quick check shared by the analyst's browser and Bun lab runner (domain/quality.ts). */
export const QUICKCHECK_MODEL = { model: "quickcheck", version: "1" } as const;
export const BROWSER_MODELS: readonly string[] = [
  modelRef(QUICKCHECK_MODEL.model, QUICKCHECK_MODEL.version),
];

export interface JobSummary {
  id: string;
  model: string;
  version: string;
  purpose: JobPurpose;
  status: JobStatus;
  attempts: number;
  maxAttempts: number;
  leaseUntil: number | null;
  lastError: string | null;
  updatedAt: number;
}

export const claimInputSchema = z.object({
  /** model@version the runner has; the key's own list limits it further. */
  models: z.array(z.string().regex(MODEL_REF)).min(1).max(50),
  limit: z.number().int().min(1).max(10).default(1),
  leaseSeconds: leaseSecondsSchema.default(DEFAULT_LEASE_SECONDS),
});
export type ClaimInput = z.infer<typeof claimInputSchema>;
/** Request body as sent: `limit` and `leaseSeconds` may be left out (zod defaults). */
export type ClaimRequest = z.input<typeof claimInputSchema>;

export const leaseInputSchema = z.object({ leaseSeconds: leaseSecondsSchema });
export type LeaseInput = z.infer<typeof leaseInputSchema>;

export interface LeaseOutcome {
  leaseUntil: number;
}

/** analysis_job.payload of classifier jobs (classify, audit_classify, classifier evaluation). */
export interface JobPayload {
  objectIds: string[];
  /** Accepted analyst objects of other photos, a few per class, as labelled examples. */
  referenceObjectIds: string[];
}

/** A photo as runners see it: the image and its geometry, never owner, land or sample data. */
export interface RunnerPhoto {
  id: string;
  sha256: string;
  kind: PhotoKind;
  group: AnalysisGroup;
  magnification: Magnification;
  umPerPixel: number;
  widthPx: number;
  heightPx: number;
  originalType: OriginalType;
  viewType: ViewType;
  fieldNumber: number | null;
  dropNumber: number | null;
  stageXUm: number | null;
  stageYUm: number | null;
}

export interface RunnerObjectRef {
  id: string;
  shape: ObjectShape;
  points: Point[];
  class: ObjectClass;
  lengthUm: number | null;
  widthUm: number | null;
  color: HyphaColor | null;
}

/** A labelled example for in-context classification, with the photo to cut it from. */
export interface RunnerReference {
  class: ObjectClass;
  shape: ObjectShape;
  points: Point[];
  photo: RunnerPhoto;
}

export interface RunnerJob {
  id: string;
  model: string;
  version: string;
  purpose: JobPurpose;
  leaseUntil: number;
  /** 1 on the first claim. */
  attempt: number;
  photo: RunnerPhoto;
  /** Classifier jobs: the objects to classify. Empty for detectors and quick checks. */
  objects: RunnerObjectRef[];
  /** Classifier jobs: accepted analyst objects per class from other photos. */
  references: RunnerReference[];
}

const runnerObjectInputSchema = z
  .object({
    shape: z.enum(OBJECT_SHAPES),
    points: pointsSchema,
    class: z.enum(OBJECT_CLASSES),
    state: z.enum(ORGANISM_STATES).nullable().default(null),
    confidence: share,
    classProbabilities: probabilitiesSchema.nullable().default(null),
    lengthUm: lengthUmSchema.nullable().default(null),
    widthUm: widthUmSchema.nullable().default(null),
    color: z.enum(HYPHA_COLORS).nullable().default(null),
  })
  .refine(fitsShape, geometryIssue);
export type RunnerObjectInput = z.infer<typeof runnerObjectInputSchema>;

const classificationInputSchema = z.object({
  objectId: z.string().min(1),
  class: z.enum(OBJECT_CLASSES),
  confidence: share,
  classProbabilities: probabilitiesSchema,
});
export type ClassificationInput = z.infer<typeof classificationInputSchema>;

/**
 * quality jobs answer `quality`; detectors `objects`; classifiers `classifications`.
 * Posting a result for a job that is already done changes nothing (`duplicate: true`).
 */
export const jobResultInputSchema = z.object({
  quality: qualityReportSchema.optional(),
  objects: z.array(runnerObjectInputSchema).max(5000).optional(),
  classifications: z.array(classificationInputSchema).max(1000).optional(),
  /** Free-form numbers kept with the result for traceability (run time, tiles, …). */
  metrics: z.record(z.string().max(64), z.number()).optional(),
});
export type JobResultInput = z.infer<typeof jobResultInputSchema>;
/** Request body as sent: runner objects and classifications may leave out defaulted fields. */
export type JobResultRequest = z.input<typeof jobResultInputSchema>;

export interface RunnerResultOutcome {
  duplicate: boolean;
  objects: number;
  classifications: number;
}

export const jobFailureInputSchema = z.object({
  error: z.string().trim().min(1).max(2000),
  /** false: no point retrying (e.g. unreadable image); the job fails at once. */
  retryable: z.boolean(),
});
export type JobFailureInput = z.infer<typeof jobFailureInputSchema>;

/** jobs: claim jobs and post results; upload: the folder uploader; labels: export training labels. */
export const RUNNER_SCOPES = ["jobs", "upload", "labels"] as const;
export type RunnerScope = (typeof RUNNER_SCOPES)[number];

/** Secrets look like `blr_<43 base64url characters>`; only their SHA-256 is stored. */
export const RUNNER_KEY_PREFIX = "blr_";

export const runnerKeyInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  scopes: z
    .array(z.enum(RUNNER_SCOPES))
    .min(1)
    .max(RUNNER_SCOPES.length)
    .refine((scopes) => new Set(scopes).size === scopes.length, "Each scope once"),
  models: z.array(z.string().regex(MODEL_REF)).max(50),
});
export type RunnerKeyInput = z.infer<typeof runnerKeyInputSchema>;

export interface RunnerKey {
  id: string;
  name: string;
  /** The first 12 characters of the secret, to recognise it. */
  prefix: string;
  scopes: RunnerScope[];
  models: string[];
  createdAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
  createdByName: string | null;
}

/** The only answer that ever carries the secret. */
export interface CreatedRunnerKey {
  key: RunnerKey;
  secret: string;
}

export const labelsQuerySchema = z.object({
  group: z.enum(ANALYSIS_GROUPS),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type LabelsQuery = z.infer<typeof labelsQuerySchema>;

/** A training label; reference-set photos are held out so the gate stays honest. */
export interface LabelRecord {
  objectId: string;
  photo: RunnerPhoto;
  shape: ObjectShape;
  points: Point[];
  class: ObjectClass;
  /** rejected: a runner suggestion the analyst rejected without correcting it. */
  status: "accepted" | "rejected";
  source: ObjectSource;
  lengthUm: number | null;
  widthUm: number | null;
  color: HyphaColor | null;
  /** Class of the runner suggestion this analyst object corrects. */
  correctedFrom: ObjectClass | null;
}

export interface LabelPage {
  labels: LabelRecord[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Pipeline, autonomy and the reference set
// ---------------------------------------------------------------------------

/**
 * Hand-over per organism group: 1 quality checks only; 2 AI suggests, the analyst confirms each
 * object; 3 the analyst reviews flagged objects and the audit sample; 4 automatic.
 */
export const AUTONOMY_LEVELS = [1, 2, 3, 4] as const;
export type AutonomyLevel = (typeof AUTONOMY_LEVELS)[number];
export const DEFAULT_AUDIT_RATE = 0.1;

const thresholdsSchema = z.partialRecord(z.enum(OBJECT_CLASSES), share);

export const pipelineStepInputSchema = z.object({
  group: z.enum(ANALYSIS_GROUPS),
  role: z.enum(STEP_ROLES),
  model: z.string().regex(MODEL_NAME),
  version: z.string().regex(MODEL_VERSION),
  defaultThreshold: share,
  /** Per-class confidence threshold, set on the reference set; others use defaultThreshold. */
  thresholds: thresholdsSchema,
});
export type PipelineStepInput = z.infer<typeof pipelineStepInputSchema>;

export const pipelineStepUpdateSchema = z.object({
  enabled: z.boolean(),
  defaultThreshold: share,
  thresholds: thresholdsSchema,
});
export type PipelineStepUpdate = z.infer<typeof pipelineStepUpdateSchema>;

export interface PipelineStep {
  id: string;
  group: AnalysisGroup;
  role: StepRole;
  model: string;
  version: string;
  enabled: boolean;
  defaultThreshold: number;
  thresholds: Partial<Record<ObjectClass, number>>;
  /** When the step last passed the reference-set gate; null when it did not (yet). */
  passedAt: number | null;
  gate: GateReport | null;
  createdAt: number;
  updatedAt: number;
}

export const autonomyInputSchema = z.object({
  level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  /** Share of new photos in the random audit sample. */
  auditRate: share,
  note: z.string().trim().max(2000),
});
export type AutonomyInput = z.infer<typeof autonomyInputSchema>;

export interface GroupAutonomy {
  group: AnalysisGroup;
  level: AutonomyLevel;
  auditRate: number;
  note: string;
  changedAt: number;
  changedByName: string | null;
}

/** Analyst corrections of detector suggestions per group and camera, last 30 days vs the 30 before. */
export interface CorrectionStat {
  group: AnalysisGroup;
  camera: string;
  reviewed: number;
  /** Rejected or corrected. */
  corrected: number;
  rate: number | null;
  previousReviewed: number;
  previousRate: number | null;
  /** The rate went up by more than RISING_CORRECTION_STEP: consider moving the group down. */
  rising: boolean;
}
export const RISING_CORRECTION_STEP = 0.05;

export interface ResearchCapture {
  referenceSet: boolean;
  sessions: CaptureSession[];
  photos: PhotoSummary[];
  autonomy: GroupAutonomy[];
  /** Enabled steps, so the editor can show what runs on new photos. */
  steps: PipelineStep[];
}

export interface PhotoSummary {
  id: string;
  sessionId: string;
  group: AnalysisGroup;
  magnification: Magnification;
  kind: PhotoKind;
  fieldNumber: number | null;
  dropNumber: number | null;
  stageXUm: number | null;
  stageYUm: number | null;
  widthPx: number;
  heightPx: number;
  umPerPixel: number;
  imagedAreaUm2: number;
  originalName: string;
  originalType: OriginalType;
  viewType: ViewType;
  bytes: number;
  sha256: string;
  capturedAt: number | null;
  createdAt: number;
  /** In the random audit sample: its objects always get an analyst and a second method. */
  audit: boolean;
  quality: PhotoQuality;
  qualityReport: QualityReport | null;
  /** Reference photos: this viewer's completion; other photos: the shared counted time. */
  countedAt: number | null;
  /** Reference photos only: suspension mL per g of sample (1:10 → 10); null when unknown. */
  dilution: number | null;
  jobs: JobSummary[];
  /**
   * Objects per status that the viewer counts: on reference-set research only their own (admins
   * see the other analysts' objects there but never count them); otherwise every visible object.
   */
  objectCounts: Record<ObjectStatus, number>;
}

export type ReferenceQueuePhoto = PhotoSummary & {
  researchId: string;
  /** Admins and above: every analyst who marked the photo done, by name; analysts: null. */
  completedBy: AnnotatorRef[] | null;
};

export interface ReferenceQueue {
  photos: ReferenceQueuePhoto[];
  done: number;
  total: number;
}

export interface PipelineOverview {
  steps: PipelineStep[];
  autonomy: GroupAutonomy[];
  health: CorrectionStat[];
}

export interface EvaluationQueued {
  /** New evaluation jobs; photos that already have one keep it (results are never recomputed). */
  queued: number;
  photos: number;
}

/** An analyst, or the step under evaluation (`id` "model"). */
export interface AnnotatorRef {
  id: string;
  name: string;
}

/** Analysts by name, then id: the order of `ReferenceQueuePhoto.completedBy` and its progress. */
export const byAnnotatorName = (a: AnnotatorRef, b: AnnotatorRef): number =>
  a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

export interface PairAgreement {
  a: AnnotatorRef;
  b: AnnotatorRef;
  /** Reference photos both annotated. */
  photos: number;
  /** Σ|a − b| / Σ((a + b) / 2) over per-photo counts of counted classes; null without counts. */
  countError: number | null;
  /** Objects matched between a and b (domain/compare.ts). */
  matched: number;
  /** Share of matched objects with the same class. */
  classAgreement: number | null;
  /** Equal states / same-class matched pairs whose class supports state. */
  stateAgreement: number | null;
  /** Fungi: equal colour / matched pairs with both colours labelled. */
  colorAgreement: number | null;
  /** Fungi: |Σ length a − Σ length b| / mean of both. */
  lengthError: number | null;
  /** Fungi: mean |width a − width b| (µm) over matched hyphae. */
  widthErrorUm: number | null;
  researches: number;
  /** Share of indicators with the same Indicator.status, over every reference research. */
  statusAgreement: number | null;
  /** Share of reference researches with the same grade. */
  gradeAgreement: number | null;
}

export interface AgreementReport {
  group: AnalysisGroup;
  researches: number;
  photos: number;
  analysts: AnnotatorRef[];
  /** Every pair of analysts who annotated the same reference photos. */
  pairs: PairAgreement[];
}

export const GATE_REASONS = [
  "no_analyst_pair",
  "no_model_output",
  "too_few_photos",
  "count_error",
  "class_agreement",
  "length_error",
  "width_error",
  "status_agreement",
  "grade_agreement",
] as const;
export type GateReason = (typeof GATE_REASONS)[number];

/** A step passes when it agrees with each analyst about as well as the analysts agree. */
export interface GateReport {
  group: AnalysisGroup;
  role: StepRole;
  model: string;
  version: string;
  analystPair: PairAgreement | null;
  modelPairs: PairAgreement[];
  passed: boolean;
  reasons: GateReason[];
  computedAt: number;
}
